////////////////////////////////////////////
// WALLET CONNECT MODAL — THE ONE SHARED CONNECT SURFACE
////////////////////////////////////////////
//
// MOUNTED ONCE IN PageShell AND OPENED FROM EVERYWHERE VIA useConnect().
// openConnect(). ONE LOOK + ONE BEHAVIOUR FOR BOTH CHAINS AND FOR DESKTOP +
// MOBILE: A CENTERED DIALOG ON DESKTOP, A BOTTOM SHEET ON PHONES. IT LISTS THE
// NORMALIZED ConnectOptions — DISCOVERED WALLETS FIRST, THEN THE CURATED
// REGISTRY AS INSTALL LINKS — AND, WHEN THE CONNECTED WALLET CAN'T SERVE THIS
// PAGE'S NETWORK (RED PILL), A BANNER SAYING WHY THE PICKER OPENED.
// ACCESSIBLE: role="dialog" + aria-modal, FOCUS TRAP, ESC + BACKDROP CLOSE.
import { useEffect, useRef } from "react";
import { useConnect, type ConnectOption } from "@/app/providers/ConnectProvider";

const ROW =
  "flex items-center gap-[11px] w-full px-3 py-[12px] bg-panel2/60 border border-line rounded-[11px] " +
  "text-fg font-semibold text-left cursor-pointer transition-colors hover:border-ember hover:bg-white/6 " +
  "disabled:opacity-55 disabled:cursor-default mb-2";

const SECTION = "font-mono uppercase tracking-[1.3px] text-[11px] text-dim px-[2px] pt-1 pb-2";

export function WalletConnectModal() {

  const c = useConnect();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // FOCUS TRAP + ESC — SET UP WHILE OPEN, TORN DOWN ON CLOSE. FOCUS RETURNS TO
  // WHATEVER OPENED IT (THE BROWSER RESTORES THE PRIOR activeElement ON UNMOUNT
  // OF THE TRAP; WE ALSO REMEMBER + RESTORE EXPLICITLY).
  useEffect(() => {

    if (!c.isConnectOpen) return;

    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {

      if (e.key === "Escape") {

        c.closeConnect();
        return;
      }
      if (e.key !== "Tab") return;

      // WRAP TAB WITHIN THE PANEL
      const nodes = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])'
      );
      if (!nodes || !nodes.length) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {

        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {

        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {

      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
    // ONLY OPEN/CLOSE DRIVES THIS — DEPENDING ON THE WHOLE CONTEXT WOULD TEAR
    // DOWN + RESTORE FOCUS TO THE OPENER ON EVERY connectingId CHANGE MID-CONNECT
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.isConnectOpen, c.closeConnect]);

  if (!c.isConnectOpen) return null;

  const title = "Connect a wallet";

  return (
    <div className="fixed inset-0 z-[1200] flex items-end justify-center min-[640px]:items-center" role="presentation">
      {/* BACKDROP — CLICK-TO-CLOSE (BACKDROP ONLY) */}
      <div className="absolute inset-0 bg-black/62 backdrop-blur-[2px]" onClick={c.closeConnect} />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wc-title"
        className="relative w-full max-w-[420px] max-h-[86dvh] flex flex-col bg-panel border border-line rounded-t-2xl min-[640px]:rounded-2xl min-[640px]:mx-4 p-5 pb-[calc(18px+env(safe-area-inset-bottom,0px))] shadow-[0_-14px_40px_rgba(0,0,0,.5)] min-[640px]:shadow-[0_20px_60px_-18px_rgba(0,0,0,.75)]"
      >
        <div className="flex items-start justify-between mb-1 flex-none">
          <h2 id="wc-title" className="text-[16px] font-bold text-fg">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            className="p-1 -mr-1 -mt-1 text-dim hover:text-fg cursor-pointer bg-transparent border-0"
            onClick={c.closeConnect}
          >
            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {/* THE LIST SCROLLS INSIDE THE SHEET — LONG WALLET LISTS MUST NEVER
            PUSH THE PANEL PAST THE VIEWPORT */}
        <div className="overflow-y-auto min-h-0 overscroll-contain">
          <ConnectBody />
        </div>
      </div>
    </div>
  );
}

////////////////////////////////////////////
// BODY — ONE FLAT, UNGROUPED WALLET LIST FOR EVERY DEVICE. ANY APP IS
// SELECTABLE REGARDLESS OF THE PAGE (A MISMATCHED PICK JUST FLAGS THE PILL
// RED AND THE MENU/BANNER GUIDE THE USER); SUBTLE IN-ROW CHAIN BADGES SHOW
// WHICH PLATFORMS EACH APP SERVES. THE ONLY GROUPS ARE THE CONNECTABLE
// CHOICES (DETECTED SORTED FIRST) AND THE CURATED "Get a wallet" INSTALLS.
////////////////////////////////////////////
function ConnectBody() {

  const c = useConnect();

  // MWA STAYS HIDDEN — ITS LOOPBACK FLOW STALLS BEHIND THE BROWSER'S
  // LOCAL-NETWORK-ACCESS PERMISSION; THE DEEPLINK SESSIONS COVER MOBILE
  const choices = c.options
    .filter((o) => o.kind !== "install" && !o.mwa)
    .sort((a, b) => Number(b.detected) - Number(a.detected));
  const installs = c.options.filter((o) => o.kind === "install");
  const hasDeeplink = choices.some((o) => o.kind === "deeplink");

  return (
    <>
      {/* WALLET-APP ROUND TRIP — THE ONE LINE OF MOBILE-ONLY CONTEXT */}
      {hasDeeplink && (
        <p className="text-[12.5px] leading-[1.55] text-dim mb-3 mt-1">
          Pick your wallet — approve in the app and you'll hop right back here.
        </p>
      )}

      {choices.length > 0 ? (
        <>
          <div className={SECTION}>Choose a wallet</div>
          {choices.map((o) => <OptionRow key={o.id} o={o} />)}
        </>
      ) : (
        <p className="text-[12.5px] leading-[1.55] text-dim mb-3 mt-1">
          No wallet detected in this browser — grab one below, then come back.
        </p>
      )}

      {/* WALLETCONNECT COULDN'T MINT A PAIRING (RELAY BLOCKED / OFFLINE) —
          SAY SO AND OFFER A RETRY INSTEAD OF LEAVING DEAD ROWS */}
      {c.wcPairing === "failed" && choices.some((o) => o.id.startsWith("wc:")) && (
        <p className="text-[11.5px] leading-normal text-warn mt-1 mb-2 px-[2px]">
          WalletConnect couldn&rsquo;t start — these wallets are unavailable right now.{" "}
          <button type="button" className="underline text-warn bg-transparent border-0 p-0 cursor-pointer" onClick={c.retryWc}>
            Try again
          </button>
        </p>
      )}

      {installs.length > 0 && (
        <>
          <div className={SECTION + (choices.length ? " pt-3" : "")}>Get a wallet</div>
          {installs.map((o) => <OptionRow key={o.id} o={o} />)}
        </>
      )}
    </>
  );
}

// ONE WALLET OPTION ROW — ICON + NAME + (SPINNER | STATE TAG). CURATED
// INSTALL ROWS ARE PLAIN LINKS TO THE WALLET'S DOWNLOAD PAGE.
function OptionRow({ o }: { o: ConnectOption }) {

  const c = useConnect();

  const mark = o.icon
    ? <img className="w-[26px] h-[26px] rounded-[7px] object-cover bg-white/8 flex-none" src={o.icon} alt="" />
    : o.color
    ? <span className="w-[26px] h-[26px] rounded-[7px] bg-white/8 inline-flex items-center justify-center flex-none"><Dot color={o.color} /></span>
    : <span className="w-[26px] h-[26px] rounded-[7px] bg-white/8 inline-flex items-center justify-center text-[14px] flex-none">{o.kind === "walletconnect" ? "◇" : "◈"}</span>;

  if (o.kind === "install")
    return (
      <a className={ROW} href={o.install} target="_blank" rel="noreferrer">
        {mark}
        <span className="flex-1">{o.name}</span>
        <ChainBadges chains={o.chains} />
        <span className="text-[10px] font-mono uppercase tracking-[1px] text-dim">get ↗</span>
      </a>
    );

  // MOBILE WALLETCONNECT ROWS FOLLOW THE PAIRING LIFECYCLE — UNTAPPABLE
  // UNTIL THE URI IS MINTED (A TAP MUST NAVIGATE SYNCHRONOUSLY), GREYED WITH
  // THE RETRY NOTE WHEN THE RELAY FAILED. "idle" (DESKTOP/TESTS) IS PLAIN.
  const wcState = o.id.startsWith("wc:") && !o.connected ? c.wcPairing : null;
  const wcBlocked = wcState === "preparing" || wcState === "failed";

  return (
    <button
      type="button"
      className={ROW + (wcState === "failed" ? " opacity-55" : "")}
      disabled={!!c.connectingId || wcBlocked}
      onClick={() => c.selectOption(o.id)}
    >
      {mark}
      <span className="flex-1">{o.name}</span>
      <ChainBadges chains={o.chains} />
      {c.connectingId === o.id
        ? <Spinner />
        : o.connected
          ? <span className="text-[10px] font-mono uppercase tracking-[1px] text-green">connected</span>
          : wcState === "preparing"
            ? <span className="text-[10px] font-mono uppercase tracking-[1px] text-dim">preparing…</span>
            : wcState === "failed"
              ? <span className="text-[10px] font-mono uppercase tracking-[1px] text-warn">unavailable</span>
              : o.kind === "walletconnect"
                ? <span className="text-[10px] font-mono uppercase tracking-[1px] text-dim">QR / mobile</span>
                : o.kind === "deeplink"
                  ? <span className="text-[10px] font-mono uppercase tracking-[1px] text-dim">wallet app</span>
                  : o.detected && <span className="text-[10px] font-mono uppercase tracking-[1px] text-green">detected</span>}
    </button>
  );
}

// SUPPORTED-PLATFORM BADGES — SUBTLE, INFORMATIONAL ONLY (PICKING AN APP
// THAT DOESN'T SERVE THIS PAGE IS ALLOWED; THE PILL FLAGS THE MISMATCH)
function ChainBadges({ chains }: { chains?: ("solana" | "rh")[] }) {

  if (!chains?.length)
    return null;
  return (
    <span className="inline-flex items-center gap-[4px] opacity-55 flex-none">
      {chains.map((ch) => (
        <img
          key={ch}
          className="w-[12px] h-[12px] object-contain rounded-[3px]"
          src={ch === "rh" ? "/robinhood-icon.png" : "/solana.svg"}
          alt={ch === "rh" ? "Robinhood Chain" : "Solana"}
          title={ch === "rh" ? "Robinhood Chain" : "Solana"}
        />
      ))}
    </span>
  );
}

// SPINNER — SHARED animate-rot RING
function Spinner() {

  return <span className="w-[16px] h-[16px] rounded-full border-2 border-white/20 border-t-ember animate-rot flex-none" />;
}

// COLORED WALLET DOT — BRAND-ACCENT FALLBACK FOR OPTIONS WITHOUT AN ICON
function Dot({ color }: { color: string }) {

  return <span className="w-[10px] h-[10px] rounded-full flex-none" style={{ background: color, boxShadow: `0 0 8px ${color}99` }} />;
}
